import React from 'react';
import Dialog from '@dashboard/components/Dialog';
import { Form } from '@dashboard/components/Formik';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { webhooksKey } from '@dashboard/lib/queryKeys';
import * as Yup from 'yup';
import CopyableText from '@/dashboard/components/CopyableText';

const WebhookDeleteDialog = ({ open, onClose, webhook }) => {
  const { sellerId, token } = useAuth();
  const queryClient = useQueryClient();

  const deleteWebhook = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/webhooks/${webhook?.id}`, token, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: webhooksKey(sellerId) });
      toast({
        title: 'Webhook deleted',
        type: 'success',
      });
      onClose();
    },
    onError: () => {
      toast({
        title: 'Failed to delete webhook',
        type: 'error',
      });
    },
  });

  return (
    <Dialog
      title="Delete webhook"
      open={open}
      onCancel={onClose}
      cancelButton={null}
      confirmButton={null}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-muted-foreground">
          Are you sure you want to delete the webhook <b>{webhook?.name}</b>? This action cannot be
          undone and will immediately stop all webhook deliveries.
        </p>

        {webhook && <CopyableText text={webhook.url} label="Webhook endpoint"></CopyableText>}

        <Form
          showState={false}
          validationSchema={Yup.object().shape({
            webhookName: Yup.string()
              .required('Webhook name is required')
              .test(
                'matches-webhook-name',
                'The webhook name entered does not match.',
                function (value) {
                  return value === webhook?.name;
                },
              ),
          })}
          initialValues={{
            webhookName: '',
          }}
          onSubmit={async () => {
            try {
              await deleteWebhook.mutateAsync();
              return { success: true };
            } catch (error) {
              return { error };
            }
          }}
          fields={[
            {
              name: 'webhookName',
              label: (
                <>
                  Type <b>{webhook?.name}</b> to confirm deletion
                </>
              ),
              type: 'text',
              required: true,
              placeholder: webhook?.name,
            },
          ]}
          formSubmitAlign="end"
          submitButton={{
            label: 'Delete webhook',
            appearance: 'secondary',
            disabled: deleteWebhook.isPending,
          }}
          cancelButton={{
            label: 'Cancel',
            appearance: 'secondary',
            onClick: onClose,
          }}
        />
      </div>
    </Dialog>
  );
};

export default WebhookDeleteDialog;
